import { photos as photosApi } from '../services/api';

// Trail photos go straight from the browser to S3: the site's CloudFront WAF
// rejects upload bodies over ~8 KB, so they can't travel through /api.
// Photos are shrunk first — a phone's 5 MB original becomes ~0.5 MB.

const MAX_EDGE = 2000;
const QUALITY = 0.85;
const PASSTHROUGH_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

async function decode(file) {
  if (window.createImageBitmap) {
    // Honours the EXIF rotation phones record instead of rotating pixels.
    try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch { /* fall through */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable')); };
    img.src = url;
  });
}

// A JPEG no larger than MAX_EDGE on its long side. Falls back to the original
// if the browser can't decode it but the format is one S3 accepts.
export async function prepareImage(file) {
  let image;
  try {
    image = await decode(file);
  } catch {
    if (PASSTHROUGH_TYPES.includes(file.type)) return file;
    throw new Error(`"${file.name}" isn't a photo format this browser can read — try a JPEG.`);
  }
  const width = image.width || image.naturalWidth;
  const height = image.height || image.naturalHeight;
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
  if (image.close) image.close();

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
  if (!blob) {
    if (PASSTHROUGH_TYPES.includes(file.type)) return file;
    throw new Error(`Couldn't process "${file.name}".`);
  }
  return blob;
}

// Resize, upload to S3, then record it against the segment. Returns the photo.
export async function uploadSegmentPhoto(segmentId, file, caption) {
  const blob = await prepareImage(file);
  const { uploadUrl, key } = await photosApi.uploadUrl(segmentId, { content_type: blob.type, size: blob.size });
  const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': blob.type }, body: blob });
  if (!put.ok) throw new Error(`Upload failed (${put.status}) — please try again.`);
  return photosApi.recordS3(segmentId, { key, caption });
}
